import type { JSX } from 'react';
import { BRICK_SPECS, effectiveSize, formatMm } from '@bw/core';
import type { BrickKind } from '@bw/core';
import { RotateCw, X } from 'lucide-react';
import { useEditor } from '../state/store.js';
import { SketchPanel } from './SketchPanel.js';
import { PlanPanel } from './PlanPanel.js';
import type { EditorController } from '../editor/controller.js';

const KINDS = Object.keys(BRICK_SPECS) as BrickKind[];
const THUMB_SCALE = 46 / 440; // the block, the widest unit, fills the thumbnail

/** Contextual to the active tool (design document C1). */
export function ToolPanel({
  run,
  controller,
  floating = false,
  onClose,
}: {
  readonly run: (id: string) => void;
  readonly controller: EditorController | null;
  readonly floating?: boolean;
  readonly onClose?: (() => void) | undefined;
}): JSX.Element {
  const tool = useEditor((s) => s.tool);
  const selectedKind = useEditor((s) => s.selectedKind);
  const previewRotation = useEditor((s) => s.previewRotation);
  const snapEnabled = useEditor((s) => s.snapEnabled);
  const setSnapEnabled = useEditor((s) => s.setSnapEnabled);
  const activeMaterialId = useEditor((s) => s.activeMaterialId);
  const setActiveMaterial = useEditor((s) => s.setActiveMaterial);
  const clipboard = useEditor((s) => s.clipboard);
  const project = useEditor((s) => s.project);
  const touch = useEditor((s) => s.touch);
  useEditor((s) => s.docRevision);

  return (
    <aside className={`panel left${floating ? ' floating' : ''}`} aria-label="Tool options">
      {floating && <div className="sheetgrip" aria-hidden />}
      {floating && onClose !== undefined && (
        <button className="panelclose" onClick={onClose} aria-label="Hide tool options">
          <X size={15} />
        </button>
      )}
      {tool === 'build' && (
        <section>
          <h2>Bricks</h2>
          <div className="bricklist">
            {KINDS.map((kind) => {
              const spec = BRICK_SPECS[kind];
              const active = selectedKind === kind;
              return (
                <button
                  key={kind}
                  className="brickbtn"
                  aria-pressed={active}
                  onClick={() => run(`tool.brick.${kind}`)}
                >
                  <span className="thumb">
                    <i
                      style={{
                        width: `${Math.max(3, spec.width * THUMB_SCALE)}px`,
                        height: `${Math.max(3, spec.height * THUMB_SCALE)}px`,
                      }}
                    />
                  </span>
                  <span>
                    <span className="name">{spec.name}</span>
                    <br />
                    <span className="dim">
                      {formatMm(spec.width)} × {formatMm(spec.height)} mm
                    </span>
                  </span>
                  <kbd>{spec.shortcut}</kbd>
                </button>
              );
            })}
          </div>
          <div className="actions">
            <button className="btn" onClick={() => run('tool.rotate')} title="Rotate preview (R)">
              <RotateCw size={14} /> Rotate · {previewRotation}°
            </button>
          </div>
          <label className="check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={snapEnabled}
              onChange={(event) => setSnapEnabled(event.target.checked)}
            />
            Smart snap <span className="dim">(hold Alt to bypass)</span>
          </label>
        </section>
      )}

      {/*
        Select and Pan lay nothing, so they carry no brick list. It used to show
        for both, but picking a unit there only arms it — the buttons could not
        even read as pressed, because that state is `tool === 'build'` — so it
        was a row of controls that appeared to do nothing. A note about the
        tool is the same shape Measure and Paste already use.
      */}
      {tool === 'select' && (
        <section>
          <h2>Select</h2>
          <p className="note">
            Click a brick, or drag around several. Shift-click adds to the selection, Ctrl-click
            toggles one, and Ctrl+A takes the whole wall. Arrows nudge what is selected, Delete
            removes it, and Ctrl+C copies the pattern.
          </p>
        </section>
      )}

      {tool === 'pan' && (
        <section>
          <h2>Pan</h2>
          <p className="note">
            Drag to move the drawing. The middle or right button, or holding Space, pans from any
            tool without leaving it. The wheel zooms, and F fits the wall to the window.
          </p>
        </section>
      )}

      {tool === 'paint' && (
        <section>
          <h2>Materials</h2>
          <div className="swatches">
            {project.materials.map((material) => (
              <button
                key={material.id}
                className="swatch"
                aria-pressed={activeMaterialId === material.id}
                onClick={() => setActiveMaterial(material.id)}
                title={material.colour}
              >
                <i style={{ background: material.colour }} />
                {material.name}
              </button>
            ))}
          </div>
          <p className="note">Choose a colour, then click laid bricks. Drag to paint a run.</p>
          <div className="actions">
            <label className="btn" style={{ border: '1px solid var(--rule)', cursor: 'pointer' }}>
              Add colour
              <input
                type="color"
                aria-label="Add a colour to the palette"
                style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                onChange={(event) => {
                  const material = project.addMaterial(event.target.value);
                  setActiveMaterial(material.id);
                  touch();
                }}
              />
            </label>
          </div>
        </section>
      )}

      {tool === 'sketch' && <SketchPanel controller={controller} />}

      {tool === 'plan' && <PlanPanel controller={controller} run={run} />}

      {tool === 'measure' && (
        <section>
          <h2>Measure</h2>
          <p className="note">
            Click a first brick corner, then a second. The pointer snaps to the closest brick&rsquo;s
            nearest corner. Click again to start another measurement.
          </p>
        </section>
      )}

      {tool === 'paste' && (
        <section>
          <h2>Paste pattern</h2>
          <p className="note">
            {clipboard === null
              ? 'Copy a selection first.'
              : `${clipboard.bricks.length} bricks · ${formatMm(clipboard.width)} × ${formatMm(clipboard.height)} mm. Click repeatedly to repeat the pattern; Esc finishes.`}
          </p>
        </section>
      )}

      {tool !== 'plan' && (
      <section>
        <h2>Preview</h2>
        <p className="readout">
          {BRICK_SPECS[selectedKind].name} · {previewRotation}°{'\n'}
          {effectiveSize(selectedKind, previewRotation)
            .map((value) => formatMm(value))
            .join(' × ')}{' '}
          mm
        </p>
      </section>
      )}
    </aside>
  );
}
